'use client';

/**
 * Intent Grid Widget — schedule-driven N-line canvas for intent mode.
 *
 * @component_role Canvas widget for intent mode's grid surface. One lane
 *   per LineItem from the ingested schedule; each lane holds a short-
 *   list of cards ordered by the Pareto frontier, with identity-override
 *   promoting the named catalogue twin to `recommend`.
 *
 * @business_rule Reads `gridResponse` from `intent-store`. Renders lanes
 *   top-to-bottom (schedules read vertically) with cards laid out
 *   horizontally within each lane. Labels are data: `recommend` (solid
 *   badge), `consider` (dashed), `alternative` (ghost). Match % is
 *   derived at render time from `spec_distance` — raw metric stays
 *   internal.
 *
 * @user_outcome Buyer uploads a schedule PDF; the canvas fills with one
 *   lane per schedule line, each showing the catalogue twin (when
 *   named) plus curated alternates. Click a card → selects in graph-
 *   selection-store → copilot details panel updates.
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain discovery (intent / grid)
 */

import { useEffect, useRef, useState } from 'react';
import { ChevronsDown, ChevronsUp, Loader2, Package, Sparkles, X } from 'lucide-react';
import { toast } from 'sonner';
import { useIntentStore } from '@/lib/stores/intent-store';
import { useExplorerLabels } from '@/lib/stores/total-matrix-store';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useIntentGrid } from '@/lib/api/hooks';
import { cn } from '@/lib/utils/cn';
import { VendorMonogram } from '@/components/vendor/vendor-monogram';
import type { CanvasWidgetProps } from '@/lib/registry/types';
import type { IntentGridLine, IntentGridProduct, PoolFacets, QuoteVendorSummary } from '@/lib/api/types';

export function IntentGridWidget(_props: CanvasWidgetProps) {
  const gridResponse = useIntentStore((s) => s.gridResponse);
  const isLoading = useIntentStore((s) => s.isLoading);
  const error = useIntentStore((s) => s.error);
  const selectedLineSid = useIntentStore((s) => s.selectedLineSid);
  const selectLine = useIntentStore((s) => s.selectLine);
  const lineSelections = useIntentStore((s) => s.lineSelections);
  const setLineSelection = useIntentStore((s) => s.setLineSelection);
  const mergeGridLine = useIntentStore((s) => s.mergeGridLine);

  const L = useExplorerLabels('intent');
  const selectNode = useGraphSelectionStore((s) => s.selectNode);

  const gridStrategy = useCapabilityStore((s) => s.strategyName('intent', 'grid'));
  const refine = useIntentGrid(gridStrategy ?? 'intent_grid');
  const [pendingRefineLine, setPendingRefineLine] = useState<string | null>(null);

  // Active quick filters per lane, keyed by line_sid → {axis: value}.
  // Chip toggles write here and fire a filters-only refine; the server
  // applies them over the cached pool (no LLM, no KNN).
  const [laneFilters, setLaneFilters] = useState<Record<string, Record<string, string>>>({});

  // Lanes the user has explicitly expanded to show all 40 candidates
  // (full Pareto + dominated) rather than the default ~15 crop.
  const [expandedLanes, setExpandedLanes] = useState<Record<string, boolean>>({});

  const handleRefine = (line: IntentGridLine, text: string) => {
    const trimmed = text.trim();
    if (!trimmed || !gridResponse) return;
    setPendingRefineLine(line.line_sid);
    refine.mutate(
      {
        conversation_id: gridResponse.conversation_id,
        intent_sid: gridResponse.intent_sid,
        refine: { line_sid: line.line_sid, text: trimmed },
      },
      {
        onSuccess: (data) => {
          const updated = (data.lines ?? []).find(
            (l) => l.line_sid === line.line_sid,
          );
          if (updated) mergeGridLine(updated);
        },
        onError: (err) => toast.error(`Refine failed: ${err.message}`),
        onSettled: () => setPendingRefineLine(null),
      },
    );
  };

  const handleFilterToggle = (line: IntentGridLine, axis: string, value: string) => {
    if (!gridResponse) return;
    const current = laneFilters[line.line_sid] ?? {};
    const isActive = current[axis] === value;
    const next = { ...current };
    if (isActive) delete next[axis];
    else next[axis] = value;
    setLaneFilters((prev) => ({ ...prev, [line.line_sid]: next }));
    setPendingRefineLine(line.line_sid);
    refine.mutate(
      {
        conversation_id: gridResponse.conversation_id,
        intent_sid: gridResponse.intent_sid,
        refine: { line_sid: line.line_sid, filters: next },
      },
      {
        onSuccess: (data) => {
          const updated = (data.lines ?? []).find((l) => l.line_sid === line.line_sid);
          if (updated) mergeGridLine(updated);
        },
        onError: (err) => toast.error(`Filter failed: ${err.message}`),
        onSettled: () => setPendingRefineLine(null),
      },
    );
  };

  const handleExpandToggle = (line: IntentGridLine) => {
    if (!gridResponse) return;
    const isExpanded = !!expandedLanes[line.line_sid];
    setExpandedLanes((prev) => ({ ...prev, [line.line_sid]: !isExpanded }));
    setPendingRefineLine(line.line_sid);
    if (isExpanded) {
      // Collapse — refresh the lane via an empty-filters refine, which
      // re-crops to the default top-K film-roll.
      refine.mutate(
        {
          conversation_id: gridResponse.conversation_id,
          intent_sid: gridResponse.intent_sid,
          refine: { line_sid: line.line_sid, filters: laneFilters[line.line_sid] ?? {} },
        },
        {
          onSuccess: (data) => {
            const updated = (data.lines ?? []).find((l) => l.line_sid === line.line_sid);
            if (updated) mergeGridLine(updated);
          },
          onError: (err) => toast.error(`Collapse failed: ${err.message}`),
          onSettled: () => setPendingRefineLine(null),
        },
      );
      return;
    }
    // Expand — fire the backend's `expand` turn mode, which returns the
    // full Pareto frontier + dominated tail (no LLM, no KNN).
    refine.mutate(
      {
        conversation_id: gridResponse.conversation_id,
        intent_sid: gridResponse.intent_sid,
        expand: { line_sid: line.line_sid, limit: null },
      },
      {
        onSuccess: (data) => {
          const updated = (data.lines ?? []).find((l) => l.line_sid === line.line_sid);
          if (updated) mergeGridLine(updated);
        },
        onError: (err) => toast.error(`Expand failed: ${err.message}`),
        onSettled: () => setPendingRefineLine(null),
      },
    );
  };

  const handleFiltersClear = (line: IntentGridLine) => {
    if (!gridResponse) return;
    setLaneFilters((prev) => ({ ...prev, [line.line_sid]: {} }));
    setPendingRefineLine(line.line_sid);
    refine.mutate(
      {
        conversation_id: gridResponse.conversation_id,
        intent_sid: gridResponse.intent_sid,
        refine: { line_sid: line.line_sid, filters: {} },
      },
      {
        onSuccess: (data) => {
          const updated = (data.lines ?? []).find((l) => l.line_sid === line.line_sid);
          if (updated) mergeGridLine(updated);
        },
        onError: (err) => toast.error(`Filter failed: ${err.message}`),
        onSettled: () => setPendingRefineLine(null),
      },
    );
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <Loader2 className="w-8 h-8 animate-spin mb-3" />
        <p className="text-sm">{L.composer_searching ?? 'Building shortlist...'}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full text-destructive">
        <p className="text-sm">{error.message}</p>
      </div>
    );
  }

  if (!gridResponse || gridResponse.lines.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <Package className="w-10 h-10 mb-3 opacity-50" />
        <p className="text-sm">
          {L.history_empty_text ?? 'Drop a schedule PDF to build a shortlist across every line.'}
        </p>
      </div>
    );
  }

  const handleCardSelect = (line: IntentGridLine, product: IntentGridProduct) => {
    selectLine(line.line_sid);
    setLineSelection(
      line.line_sid,
      lineSelections[line.line_sid] === product.semantic_id ? null : product.semantic_id,
    );
    // Route to the shared ProductDetails copilot card — same card
    // discovery uses. Pass the full grid-card shape on properties so
    // details can optionally surface tier / match% if it wants. The
    // line query travels alongside so the axis-breakdown section can
    // caption the breakdown with what the buyer was searching for.
    selectNode({
      id: product.semantic_id,
      label: product.name,
      type: 'product',
      properties: {
        ...(product as unknown as Record<string, unknown>),
        line_query: line.query,
      },
    });
  };

  return (
    <div className="h-full overflow-auto p-2 space-y-1.5">
      <div className="text-[11px] text-muted-foreground px-1">
        {`${gridResponse.lines.length} lines · ${Object.keys(lineSelections).length} selected`}
      </div>
      {gridResponse.lines.map((line) => (
        <Lane
          key={line.line_sid}
          line={line}
          focused={selectedLineSid === line.line_sid}
          selectedSid={lineSelections[line.line_sid] ?? null}
          refining={pendingRefineLine === line.line_sid}
          activeFilters={laneFilters[line.line_sid] ?? {}}
          expanded={!!expandedLanes[line.line_sid]}
          onCardSelect={(p) => handleCardSelect(line, p)}
          onHeaderClick={() =>
            selectLine(selectedLineSid === line.line_sid ? null : line.line_sid)
          }
          onRefine={(text) => handleRefine(line, text)}
          onFilterToggle={(axis, value) => handleFilterToggle(line, axis, value)}
          onFiltersClear={() => handleFiltersClear(line)}
          onExpandToggle={() => handleExpandToggle(line)}
        />
      ))}
    </div>
  );
}

// ============================================================
// Lane — one LineItem's shortlist
// ============================================================

function Lane({
  line,
  focused,
  selectedSid,
  refining,
  activeFilters,
  expanded,
  onCardSelect,
  onHeaderClick,
  onRefine,
  onFilterToggle,
  onFiltersClear,
  onExpandToggle,
}: {
  line: IntentGridLine;
  focused: boolean;
  selectedSid: string | null;
  refining: boolean;
  activeFilters: Record<string, string>;
  expanded: boolean;
  onCardSelect: (p: IntentGridProduct) => void;
  onHeaderClick: () => void;
  onRefine: (text: string) => void;
  onFilterToggle: (axis: string, value: string) => void;
  onFiltersClear: () => void;
  onExpandToggle: () => void;
}) {
  // Scroll into view when the explorer sets this lane as focused
  // (selectedLineSid bound from intent-schedules-section click).
  const ref = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (focused && ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [focused]);

  const [refineText, setRefineText] = useState('');
  const handleRefineSubmit = () => {
    if (refineText.trim() && !refining) {
      onRefine(refineText);
      setRefineText('');
    }
  };

  return (
    <section
      ref={ref}
      className={cn(
        'rounded border bg-card',
        focused ? 'border-primary' : 'border-border',
      )}
    >
      <button
        type="button"
        onClick={onHeaderClick}
        className="w-full flex items-center justify-between px-3 py-2 text-left border-b border-border hover:bg-accent/30 transition-colors cursor-pointer"
      >
        <div className="min-w-0 flex-1 flex items-baseline gap-2">
          <p className="text-xs font-medium truncate">{line.query}</p>
          <p className="text-[10px] text-muted-foreground tracking-wider flex-shrink-0">
            {`${line.products.length}/${line.candidate_pool_size}`}
          </p>
        </div>
      </button>

      {focused && (
        <div className="px-3 py-2 border-b border-border bg-muted/30 space-y-2">
          <div className="flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5 text-primary flex-shrink-0" />
            <input
              type="text"
              value={refineText}
              onChange={(e) => setRefineText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleRefineSubmit();
              }}
              placeholder='e.g. "warmer tone, under $4k, drop marble"'
              disabled={refining}
              className={cn(
                'flex-1 px-2 py-1 text-xs rounded-md',
                'bg-background border border-input',
                'focus:outline-none focus:ring-1 focus:ring-ring',
                'disabled:opacity-50',
              )}
            />
            <button
              type="button"
              onClick={handleRefineSubmit}
              disabled={refining || !refineText.trim()}
              className={cn(
                'px-2 py-1 text-[10px] rounded-md',
                'bg-primary text-primary-foreground hover:bg-primary/90',
                'disabled:opacity-50 disabled:cursor-not-allowed',
                'flex items-center gap-1',
              )}
            >
              {refining ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
              Refine
            </button>
          </div>
          <ChipRow
            facets={line.pool_facets ?? null}
            active={activeFilters}
            disabled={refining}
            onToggle={onFilterToggle}
            onClear={onFiltersClear}
          />
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-muted-foreground">
              {expanded
                ? `Showing all ${line.products.length}/${line.candidate_pool_size}`
                : `Showing ${line.products.length}/${line.candidate_pool_size}`}
            </span>
            <button
              type="button"
              onClick={onExpandToggle}
              disabled={refining || line.candidate_pool_size <= line.products.length}
              className={cn(
                'flex items-center gap-1 text-[10px] text-muted-foreground',
                'hover:text-foreground transition-colors',
                'disabled:opacity-50 disabled:cursor-not-allowed',
              )}
              title={expanded
                ? 'Collapse to top picks'
                : `Show all ${line.candidate_pool_size} in pool`}
            >
              {expanded ? (
                <>
                  <ChevronsUp className="w-3 h-3" /> Collapse
                </>
              ) : (
                <>
                  <ChevronsDown className="w-3 h-3" /> Show all
                </>
              )}
            </button>
          </div>
        </div>
      )}

      <div className="flex gap-2 overflow-x-auto p-2">
        {line.products.map((p) => (
          <LaneCard
            key={p.semantic_id}
            product={p}
            selected={selectedSid === p.semantic_id}
            onSelect={() => onCardSelect(p)}
          />
        ))}
        {(line.quote_vendors ?? []).map((v) => (
          <QuoteVendorCard key={v.vendor_sid ?? v.vendor_name} vendor={v} />
        ))}
      </div>
    </section>
  );
}

// ============================================================
// LaneCard — product card with identity badge + match %
// ============================================================

function LaneCard({
  product,
  selected,
  onSelect,
}: {
  product: IntentGridProduct;
  selected: boolean;
  onSelect: () => void;
}) {
  const specPct = matchPercent(product.spec_distance);
  const formPct = matchPercent(product.form_distance ?? null);
  return (
    <div
      onClick={onSelect}
      className={cn(
        'group flex-shrink-0 w-36 p-2 rounded-lg border border-panel-border bg-card cursor-pointer',
        'hover:border-primary/50 hover:shadow-md transition-all',
        'text-left flex flex-col',
        selected && 'ring-2 ring-primary border-primary',
      )}
    >
      <div className="aspect-square rounded-md overflow-hidden bg-muted mb-2 relative">
        {product.image_url ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={product.image_url}
            alt={product.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Package className="w-5 h-5 text-muted-foreground/50" />
          </div>
        )}
        <LabelBadge label={product.label} />
      </div>
      <p className="text-xs font-medium line-clamp-2 leading-tight">{product.name}</p>
      <div className="mt-1 flex items-center justify-between gap-1 text-[10px] text-muted-foreground">
        {product.price != null && <span>${Number(product.price).toFixed(0)}</span>}
        {/* Two numbers when form is active, one when it isn't (pareto_
            three_axis_concept.md's Costs section) — formPct is only
            non-null when the BFF had a real image on both sides, never
            the missing-axis placeholder used internally for ranking. */}
        {formPct != null ? (
          <span>Spec {specPct}% · Form {formPct}%</span>
        ) : (
          specPct != null && <span>{specPct}% match</span>
        )}
      </div>
    </div>
  );
}

/**
 * Label badge — `recommend` / `consider` / `alternative` follow the
 * design-token status pattern (`bg-{color}/20 text-{color}` for non-
 * CTA states; filled primary for the CTA).
 *   recommend   → filled primary (the pick)
 *   consider    → info-tinted (curated alternate, informational)
 *   alternative → muted (long-tail / fallback)
 */
function LabelBadge({ label }: { label: IntentGridProduct['label'] }) {
  const styles: Record<IntentGridProduct['label'], string> = {
    recommend: 'bg-primary text-primary-foreground',
    consider: 'bg-info/20 text-info',
    alternative: 'bg-muted text-muted-foreground',
  };
  return (
    <span
      className={cn(
        'absolute top-1.5 left-1.5',
        'text-[10px] font-medium tracking-wider uppercase',
        'rounded px-1.5 py-0.5',
        styles[label],
      )}
    >
      {label}
    </span>
  );
}

// ============================================================
// QuoteVendorCard — price-on-request rollup, not a scored product
// ============================================================

/**
 * One vendor's price-on-request rollup — replaces individual POA
 * cards (pareto_three_axis_concept.md's "price-on-request becomes a
 * per-vendor rollup" section). Same footprint as `LaneCard` so it
 * sits naturally at the end of the lane's horizontal scroll, but
 * paints the vendor's real logo-or-monogram (same `VendorMonogram`
 * component every other vendor card in the app uses) instead of a
 * product photo, and deliberately carries no match%, no
 * `recommend`/`consider`/`alternative` badge — this isn't a Pareto-
 * scored point, it's an index into a vendor's unpriced catalogue.
 */
function QuoteVendorCard({ vendor }: { vendor: QuoteVendorSummary }) {
  return (
    <div
      className={cn(
        'flex-shrink-0 w-36 p-2 rounded-lg border border-dashed border-panel-border bg-card/50',
        'text-left flex flex-col items-center justify-center text-center',
      )}
    >
      <div className="aspect-square w-full rounded-md overflow-hidden bg-muted mb-2 flex items-center justify-center p-3">
        <VendorMonogram
          name={vendor.vendor_name}
          initials={vendor.monogram}
          bg={vendor.monogram_bg}
          fg={vendor.monogram_fg}
          logo={vendor.logo}
          className={vendor.logo ? 'h-full w-full' : 'h-12 w-12 text-sm'}
        />
      </div>
      <p className="text-xs font-medium line-clamp-2 leading-tight">{vendor.vendor_name}</p>
      <p className="mt-1 text-[10px] text-muted-foreground">
        +{vendor.count} more · Price on request
      </p>
    </div>
  );
}

// ============================================================
// Helpers
// ============================================================

/** Convert spec_distance (0 = identical, 1 = no match) to match percent. */
function matchPercent(spec_distance: number | null): number | null {
  if (spec_distance == null) return null;
  const pct = Math.round((1 - spec_distance) * 100);
  if (pct < 0) return 0;
  if (pct > 100) return 100;
  return pct;
}

// ============================================================
// ChipRow — quick filters grounded in the lane's live pool
// ============================================================

/**
 * Three-axis chip row: material, color, product_subtype. Each axis
 * shows the top-3 most common values from the cached pool so chips are
 * always filterable (no empty-result surprise). Backend filter semantics
 * are absolute-set — the chip bank reflects the full selection the
 * refine call will ship, not a delta. Vendor intentionally omitted
 * here: vendor names are long and crowd the row; live with that until
 * a use case asks for it.
 */
function ChipRow({
  facets,
  active,
  disabled,
  onToggle,
  onClear,
}: {
  facets: PoolFacets | null;
  active: Record<string, string>;
  disabled: boolean;
  onToggle: (axis: string, value: string) => void;
  onClear: () => void;
}) {
  if (!facets || !facets.axes || facets.axes.length === 0) return null;
  const anyActive = Object.values(active).some(Boolean);

  return (
    <div className="space-y-1">
      {facets.axes.map((axis) => (
        <div key={axis.key} className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[9px] uppercase tracking-wider text-muted-foreground w-12 flex-shrink-0">
            {axis.label}
          </span>
          {axis.values.slice(0, 4).map((v) => {
            // Active-state and onToggle use the canonical English `raw`
            // value so filter predicates stay language-stable. The
            // chip face shows the localized `value`.
            const isActive = active[axis.key] === v.raw;
            return (
              <button
                key={v.raw}
                type="button"
                disabled={disabled}
                onClick={() => onToggle(axis.key, v.raw)}
                className={cn(
                  'text-[10px] px-2 py-0.5 rounded-full transition-colors',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                  isActive
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-background border border-border hover:bg-accent',
                )}
                title={`${v.count} in pool`}
              >
                {v.value}
                <span className="ml-1 opacity-60">{v.count}</span>
              </button>
            );
          })}
        </div>
      ))}
      {anyActive && (
        <button
          type="button"
          onClick={onClear}
          disabled={disabled}
          className={cn(
            'flex items-center gap-1 text-[10px] text-muted-foreground',
            'hover:text-foreground transition-colors',
            'disabled:opacity-50',
          )}
        >
          <X className="w-3 h-3" />
          Clear filters
        </button>
      )}
    </div>
  );
}
